import {
  FirebaseAnalyticsClient,
  LoggerHelper,
  LogRepository,
  Role,
  Roles,
  SessionService,
  useCoreContext,
  UserRole,
} from '@src/core';
import { FirebaseAuthenticationClient } from '@src/core/clients/firebase-authentication';
import type { UserAuth } from '@src/core/clients/firebase-authentication';
import { createContext, FC, PropsWithChildren, useContext, useEffect, useReducer, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

const AUTH_AUTHENTICATE_FAILED = 'AUTHENTICATE_FAILED';
const AUTH_AUTHENTICATED = 'AUTHENTICATED';
const AUTH_AUTHORIZED = 'AUTHORIZED';
const AUTH_UNAUTHENTICATED = 'UNAUTHENTICATED';

type AuthActions =
  | typeof AUTH_AUTHENTICATE_FAILED
  | typeof AUTH_AUTHENTICATED
  | typeof AUTH_AUTHORIZED
  | typeof AUTH_UNAUTHENTICATED;

const authReducer = (state: AuthState, action: AuthActions) => {
  switch (action) {
    case AUTH_AUTHENTICATE_FAILED:
      return {
        authentication: false,
        authorization: false,
      };
    case AUTH_AUTHENTICATED:
      return {
        authentication: true,
        authorization: false,
      };
    case AUTH_AUTHORIZED:
      return {
        authentication: true,
        authorization: true,
      };
    case AUTH_UNAUTHENTICATED:
      return {
        authentication: false,
        authorization: false,
      };
    default:
      return state;
  }
};

interface AuthState {
  authentication: boolean;
  authorization: boolean;
}

const initialAuthState: AuthState = {
  authentication: false,
  authorization: false,
};

export const SessionContext = createContext<any>(null);

const SessionProvider: FC<PropsWithChildren> = ({ children }) => {
  const { realtimeDbPath } = useCoreContext();
  const navigate = useNavigate();
  const location = useLocation();

  // state
  const [auth, dispatchAuth] = useReducer(authReducer, initialAuthState);
  const [validating, setValidating] = useState<boolean>(true);

  // user data
  const [name, setName] = useState('');
  const [initial, setInitial] = useState('');
  const [email, setEmail] = useState('');
  const [permission, setPermission] = useState<Role | string>('');
  const [contactName, setContactName] = useState('');
  const [contactNamePrefix, setContactNamePrefix] = useState('');
  const [userId, setUserId] = useState('');
  const [projectsAllowed, setProjectsAllowed] = useState<string[]>([]);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isManager, setIsManager] = useState(false);

  /**
   * Conta os sign-in desta aba, para quem precisa reagir a "entrou agora".
   *
   * É contador, e não um booleano ou o nome do último evento, porque num
   * sign-in novo validateUser roda duas vezes: uma pelo doSignIn, outra pelo
   * listener do Firebase. Um campo de último evento terminaria em 'validate' e
   * quem observa poderia nunca ver o 'login'. Um número que só avança no ramo
   * de login muda exatamente uma vez, qualquer que seja o entrelaçamento.
   */
  const [signInCount, setSignInCount] = useState(0);

  useEffect(() => {
    validateSession();
  }, []);

  const validateSession = () => {
    setValidating(true);
    FirebaseAuthenticationClient.onStateChange((firebaseUser: UserAuth | null) => {
      if (!firebaseUser) {
        navigate('/login');
        setValidating(false);
        return;
      }
      validateUser(firebaseUser, 'validate');
    });
  };

  const doSignIn = () => {
    setValidating(true);
    FirebaseAuthenticationClient.singInGoogle()
      .then((result: { user: any }) => {
        const { user } = result;
        validateUser(user, 'login');
      })
      .catch((err: any) => {
        LoggerHelper.error('sign-in error', err);
        setValidating(false);
      });
  };

  const doSignOut = () => {
    FirebaseAuthenticationClient.singOut()
      .then(() => {
        /**
         * Recarrega a aplicacao na raiz em vez de navegar pelo router: o reload
         * descarta todo o estado em memoria -- sessao, projeto, cache e
         * interacao -- e o SessionProvider remonta sem usuario, caindo no login
         * limpo. Navegar pelo router deixaria os dados do usuario anterior
         * vivos nos contextos.
         */
        window.location.replace('/');
      })
      .catch((err: any) => LoggerHelper.error('sign-out error', err));
  };

  const validateUser = async (firebaseUser: UserAuth, event: 'login' | 'validate') => {
    setValidating(true);
    const { email, displayName } = firebaseUser || {};

    if (email === null) {
      dispatchAuth(AUTH_AUTHENTICATE_FAILED);
      setValidating(false);
      navigate('/login');
      return;
    }

    setInitial(`${displayName}`.substring(0, 1));
    setName(`${displayName}`);
    setEmail(`${email}`);

    dispatchAuth(AUTH_AUTHENTICATED);

    let user = null;
    try {
      user = await SessionService.resolve();
      // eslint-disable-next-line no-empty
    } catch (e: any) {
      LoggerHelper.error('Error fetching user data', e);
    }

    if (user === null) {
      setValidating(false);
      navigate('/login');
      return;
    }

    setPermission(user.permission);
    setIsAdmin(user.permission === UserRole.admin);
    setIsManager(user.permission === UserRole.manager);
    setContactName(user.name);
    setContactNamePrefix(user.namePrefix);
    setUserId(user.id);
    setProjectsAllowed(user.projects);

    switch (event) {
      case 'login':
        FirebaseAnalyticsClient.appLogin(user.id);
        LogRepository.logIn(user.id, realtimeDbPath);
        setSignInCount(count => count + 1);
        break;
      case 'validate':
        FirebaseAnalyticsClient.validateLogin(user.id);
        LogRepository.validateAccess(user.id, realtimeDbPath);
        break;
    }

    dispatchAuth(AUTH_AUTHORIZED);
    setValidating(false);

    if (location.pathname === '/login') {
      navigate('/');
    }
  };

  const allowAccess = (roles: Roles) => roles.includes(permission as Role);

  /**
   * Perfil somente leitura: a interface esconde as acoes de escrita e o
   * backend as recusa pelo WriteAccessGuard. As duas camadas existem de
   * proposito -- a tela evita oferecer o que vai falhar, e o guard e quem
   * de fato protege o dado.
   */
  const isReadonly = permission === UserRole.readonly;
  const canWrite = permission !== '' && !isReadonly;

  return (
    <SessionContext.Provider
      value={{
        userAuthentication: auth.authentication,
        userAuthorization: auth.authorization,
        userLogged: auth.authentication && auth.authorization,
        name,
        initial,
        email,
        doSignIn,
        doSignOut,
        validateSession,
        validating,
        contactName,
        contactNamePrefix,
        userId,
        allowAccess,
        projectsAllowed,
        signInCount,
        isAdmin,
        isManager,
        isReadonly,
        canWrite,
      }}
    >
      {children}
    </SessionContext.Provider>
  );
};

interface SessionContextProps {
  userAuthentication: boolean;
  userAuthorization: boolean;
  userLogged: boolean;
  name: string;
  initial: string;
  email: string;
  doSignIn: () => void;
  doSignOut: () => void;
  validateSession: () => void;
  validating: boolean;
  contactName: string;
  contactNamePrefix: string;
  userId: string;
  allowAccess: (roles: Roles) => boolean;
  projectsAllowed: string[];
  /** Avança a cada sign-in novo desta aba; 0 enquanto nenhum ocorreu. */
  signInCount: number;
  isAdmin: boolean;
  isManager: boolean;
  isReadonly: boolean;
  canWrite: boolean;
}

const useSessionContext = (): SessionContextProps => useContext(SessionContext);

export { SessionProvider, useSessionContext };
